'use client';

import { GoogleOAuthProvider } from '@react-oauth/google';
import { useRouter } from 'next/router';
import type { ReactNode } from 'react';

// Locale for Google's own UI (the account-chooser popup), which uses BCP 47 tags — our locale slugs are
// not all valid there (`cn`, `jp`, `kr`, `sa`, `pt`), hence the map.
const GOOGLE_SSO_LOCALE_MAP: Record<string, string> = {
  pt: 'pt-BR',
  en: 'en',
  es: 'es',
  ru: 'ru',
  cn: 'zh-CN',
  fr: 'fr',
  jp: 'ja',
  kr: 'ko',
  sa: 'ar',
  tr: 'tr',
};

/**
 * Mounts Google's OAuth context around whatever actually needs it.
 *
 * This used to wrap the entire app in _app.tsx, which meant `@react-oauth/google` injected
 * https://accounts.google.com/gsi/client — 97 KB of third-party script, measured — into every page of
 * the site on first load, including pages with no sign-in anywhere on them. On mobile that lands inside
 * the request window Lighthouse charges against LCP, and its evaluation lands inside the Total Blocking
 * Time window that is the only thing capping the desktop score.
 *
 * Now the script loads when something that can actually use it renders: the SSO button block
 * (sso-providers), the connections page, or the mobile auth handoff. In the modals that means the script
 * starts on open, while the user is still reading the form, rather than on every page view.
 *
 * `key` on the locale is deliberate and predates this change: the provider caches Google's script config,
 * so switching language has to remount it for the popup to follow.
 */
export function GoogleOAuthScope({ children }: { children: ReactNode }) {
  const router = useRouter();
  const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ?? '';
  const locale = GOOGLE_SSO_LOCALE_MAP[router.locale ?? 'en'] ?? 'en';

  return (
    <GoogleOAuthProvider key={locale} clientId={clientId} locale={locale}>
      {children}
    </GoogleOAuthProvider>
  );
}
